import { Box, Flex, Heading, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import { ScoreBadge } from 'shared-components';
import { IRecordedInterviewUpdateScoresPayload, ScoreTypeEnum } from 'api-paths-cas';
import { ScoreDetailsActionButtons } from './ScoreDetailsActionButtons';
import { ScoreDetailsExplanation } from './ScoreDetailsExplanation';

export type EditQuestionScorePayload = Omit<IRecordedInterviewUpdateScoresPayload, 'questionNo'>;

interface ScoreDetailsProps {
  type: ScoreTypeEnum;
  value?: number;
  withIndicator?: boolean;
  explanation?: string;
  transcript?: string;
  onSave: (payload: EditQuestionScorePayload) => void;
}
function ScoreDetails(props: ScoreDetailsProps) {
  const { type, value, explanation, withIndicator, transcript, onSave } = props;

  const { isOpen, onOpen, onClose, getDisclosureProps, getButtonProps } = useDisclosure();
  const editDisclosureProps = getDisclosureProps();
  const buttonDisclosureProps = getButtonProps();

  const [currentValue, setCurrentValue] = useState(value ?? 0);
  const [currentExplanation, setCurrentExplanation] = useState(explanation ?? '');

  const resetState = useCallback((value?: number, explanation?: string) => {
    setCurrentValue(value ?? 0);
    setCurrentExplanation(explanation ?? '');
  }, []);

  useEffect(() => {
    resetState(value, explanation);
  }, [value, explanation, resetState]);

  const onSaveHandler = useCallback(() => {
    onSave({ type, score: currentValue, explanation: currentExplanation });
    onClose();
  }, [currentExplanation, currentValue, onClose, onSave, type]);

  const onCloseHandler = useCallback(() => {
    resetState(value, explanation);
    onClose();
  }, [explanation, onClose, resetState, value]);

  return (
    <Box bgColor="chakra-subtle-bg" alignSelf="stretch" borderRadius="xl" px="6" py="6">
      <Flex alignItems="center" gap="4">
        <Box mr="2">
          <ScoreBadge withIndicator={withIndicator} size="8" value={value} />
        </Box>
        <Heading flexGrow="1" as="h4" fontSize="lg" fontWeight="medium">
          {type}
        </Heading>

        <ScoreDetailsActionButtons
          buttonDisclosureProps={buttonDisclosureProps}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onCloseHandler}
          onSave={onSaveHandler}
        />
      </Flex>
      <ScoreDetailsExplanation
        editDisclosureProps={editDisclosureProps}
        currentValue={currentValue}
        setCurrentValue={setCurrentValue}
        currentExplanation={currentExplanation}
        setCurrentExplanation={setCurrentExplanation}
        isOpen={isOpen}
        type={type}
        transcript={transcript}
      />
    </Box>
  );
}

export default ScoreDetails;
